import { useState, useEffect } from "react";
import {
  useConnect,
  useDisconnect,
  useAccount,
  useSignTypedData,
  useChainId,
} from "wagmi";
import { ethers } from "ethers";
import { toSatoshi } from "../sdk/utils/math";
import { mormSigner } from "../sdk";
import { detectWallet, isPhantomAvailable, isOneKeyAvailable } from "../sdk/utils/extdetection";
import { isMetaMaskProviderAvailable } from "../hooks";
import {
  OpSubmitOrder,
  OpCancelOrder,
  OpModifyOrder,
  OpChangeLeverage,
  OpClosePosition,
  OpSwap,
  OpChangeMarginRatio,
  OpInternTransfer,
  OpBankBucketTransfer,
  OpBankExtractToBucket,
  OpBankWithdrawFromBucket,
  OpBankMint,
  OpBankTransfer,
} from "../sdk/types";

// Operation type definitions
type OperationType =
  | "placeOrder"
  | "cancelOrder"
  | "modifyOrder"
  | "changeLeverage"
  | "closePosition"
  | "swap"
  | "changeMarginRatio"
  | "internTransfer"
  | "bucketTransfer"
  | "extractToBucket"
  | "withdrawFromBucket"
  | "bankMint"
  | "bankTransfer";

interface OperationConfig {
  label: string;
  action: string;
  fields: FieldConfig[];
}

interface FieldConfig {
  key: string;
  label: string;
  type: "text" | "number" | "select";
  placeholder?: string;
  options?: string[];
  required?: boolean;
  autoGenerate?: boolean;
  convertToSatoshi?: boolean;
}

const OPERATION_CONFIGS: Record<OperationType, OperationConfig> = {
  placeOrder: {
    label: "Place Order",
    action: OpSubmitOrder,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "side",
        label: "Side",
        type: "select",
        options: ["BUY", "SELL"],
        required: true,
      },
      {
        key: "orderType",
        label: "Order Type",
        type: "select",
        options: ["LIMIT", "MARKET"],
        required: true,
      },
      {
        key: "price",
        label: "Price",
        type: "number",
        placeholder: "100.5",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "quantity",
        label: "Quantity",
        type: "number",
        placeholder: "1.0",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  cancelOrder: {
    label: "Cancel Order",
    action: OpCancelOrder,
    fields: [
      { key: "orderId", label: "Order ID", type: "text", required: true },
      { key: "marketId", label: "Market ID", type: "text", required: true },
    ],
  },
  modifyOrder: {
    label: "Modify Order",
    action: OpModifyOrder,
    fields: [
      { key: "orderId", label: "Order ID", type: "text", required: true },
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "newPrice",
        label: "New Price",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "newQuantity",
        label: "New Quantity",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  changeLeverage: {
    label: "Change Leverage",
    action: OpChangeLeverage,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "newLeverage",
        label: "New Leverage",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  closePosition: {
    label: "Close Position",
    action: OpClosePosition,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      {
        key: "quantity",
        label: "Quantity",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "marketPrice",
        label: "Market Price",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  swap: {
    label: "Swap",
    action: OpSwap,
    fields: [
      { key: "fromToken", label: "From Token", type: "text", required: true },
      { key: "toToken", label: "To Token", type: "text", required: true },
      {
        key: "amountIn",
        label: "Amount In",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "minAmountOut",
        label: "Min Amount Out",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  changeMarginRatio: {
    label: "Change Margin Ratio",
    action: OpChangeMarginRatio,
    fields: [
      { key: "marketId", label: "Market ID", type: "text", required: true },
      { key: "bucketId", label: "Bucket ID", type: "text", required: true },
      { key: "positionId", label: "Position ID", type: "text", required: false },
    ],
  },
  internTransfer: {
    label: "Internal Transfer",
    action: OpInternTransfer,
    fields: [
      {
        key: "fromAccount",
        label: "From Account",
        type: "text",
        required: true,
      },
      { key: "toAccount", label: "To Account", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
      {
        key: "tokenAddress",
        label: "Token Address",
        type: "text",
        required: true,
      },
    ],
  },
  bucketTransfer: {
    label: "Bucket Transfer",
    action: OpBankBucketTransfer,
    fields: [
      {
        key: "fromBucketId",
        label: "From Bucket ID",
        type: "text",
        required: true,
      },
      {
        key: "toBucketId",
        label: "To Bucket ID",
        type: "text",
        required: true,
      },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  extractToBucket: {
    label: "Extract to Bucket",
    action: OpBankExtractToBucket,
    fields: [
      { key: "bucketId", label: "Bucket ID", type: "text", required: true },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  withdrawFromBucket: {
    label: "Withdraw from Bucket",
    action: OpBankWithdrawFromBucket,
    fields: [
      { key: "bucketId", label: "Bucket ID", type: "text", required: true },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  bankMint: {
    label: "Bank Mint",
    action: OpBankMint,
    fields: [
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
  bankTransfer: {
    label: "Bank Transfer",
    action: OpBankTransfer,
    fields: [
      { key: "to", label: "To Address", type: "text", required: true },
      { key: "assetId", label: "Asset ID", type: "text", required: true },
      {
        key: "amount",
        label: "Amount",
        type: "number",
        required: true,
        convertToSatoshi: true,
      },
    ],
  },
};

function FunctionalSigner(): JSX.Element {
  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { address: wagmiAddress, isConnected: wagmiIsConnected } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const chainId = useChainId();

  // Step 1: Operation selection
  const [selectedOperation, setSelectedOperation] =
    useState<OperationType | null>(null);

  // Step 2: Form fields
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [mnemonic, setMnemonic] = useState<string>(
    "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about",
  );
  const [ownerAddress, setOwnerAddress] = useState<string>("");
  const [nonce, setNonce] = useState<string>("");
  const [deadline, setDeadline] = useState<string>("");
  const [availableAddresses, setAvailableAddresses] = useState<string[]>([]);

  // Step 3: Signing
  const [signingResult, setSigningResult] = useState<{
    success: boolean;
    signature?: string;
    typedData?: any;
    error?: string;
  } | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSigningWasm, setIsSigningWasm] = useState(false);

  // Wallet detection - using shared utility
  const isMetaMaskDetected = isMetaMaskProviderAvailable();
  const isPhantomDetected = isPhantomAvailable();
  const isOneKeyDetected = isOneKeyAvailable();

  const injectedConnector = connectors.find((c) => c.id === "injected");

  // Determine connected wallet using shared utility
  const detectedWallet = detectWallet();

  const hasMatchingAddress =
    ownerAddress &&
    wagmiAddress &&
    ownerAddress.toLowerCase() === wagmiAddress.toLowerCase();

  const isWalletConnected =
    wagmiIsConnected && hasMatchingAddress && ownerAddress;

  // Reset form when operation changes
  useEffect(() => {
    if (selectedOperation) {
      const config = OPERATION_CONFIGS[selectedOperation];
      const initialData: Record<string, string> = {};
      config.fields.forEach((field) => {
        initialData[field.key] = "";
      });
      setFormData(initialData);
      setSigningResult(null);
    }
  }, [selectedOperation]);

  // Auto-generate deadline
  const generateDeadline = () => {
    const now = Math.floor(Date.now() / 1000);
    const oneHourLater = now + 3600;
    setDeadline(oneHourLater.toString());
  };

  // Auto-generate nonce
  const generateNonce = () => {
    setNonce(Date.now().toString());
  };

  // Generate owner address from mnemonic
  const generateOwnerAddress = async () => {
    if (!mnemonic.trim()) {
      alert("Please enter a mnemonic phrase");
      return;
    }

    try {
      const { mormSigner } = await import("../sdk");
      await mormSigner.initialize();
      const result = await mormSigner.generateAddress(mnemonic, 0);
      if (result.success && result.address) {
        setOwnerAddress(result.address);
      } else {
        alert(result.error || "Failed to generate address");
      }
    } catch (error: any) {
      alert(error.message || "Failed to generate address");
    }
  };

  // Handle wallet connection
  const handleConnectWallet = async () => {
    if (!isMetaMaskDetected && !isPhantomDetected && !isOneKeyDetected) {
      alert("No supported wallet detected");
      return;
    }

    try {
      const ethereum = (window as any).ethereum;
      let provider = ethereum;

      // Handle Phantom
      if (isPhantomDetected) {
        provider =
          (window as any).phantom?.ethereum ||
          ((window as any).ethereum?.isPhantom ? ethereum : null);
      }

      // Handle OneKey
      if (isOneKeyDetected) {
        provider = (window as any).$onekey?.ethereum || ethereum;
      }

      if (!provider) {
        alert("Wallet provider not found");
        return;
      }

      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        setAvailableAddresses(accounts);
        if (accounts.length === 1) {
          setOwnerAddress(accounts[0]);
        }
      }

      if (injectedConnector) {
        await connect({ connector: injectedConnector });
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error: any) {
      if (error.code === 4001) {
        alert("User rejected the connection request");
      } else {
        console.error("Error connecting wallet:", error);
        alert(`Failed to connect: ${error.message || "Unknown error"}`);
      }
    }
  };

  // Build payload from form data
  const buildPayload = (): Record<string, any> => {
    if (!selectedOperation) {
      throw new Error("No operation selected");
    }

    const config = OPERATION_CONFIGS[selectedOperation];
    const payload: Record<string, any> = {
      action: config.action,
    };

    // Add address field for operations that need it
    if (
      selectedOperation === "cancelOrder" ||
      selectedOperation === "changeLeverage" ||
      selectedOperation === "changeMarginRatio" ||
      selectedOperation === "internTransfer"
    ) {
      payload.address = ownerAddress;
    }

    // Process each field
    config.fields.forEach((field) => {
      const value = formData[field.key];
      if (value === undefined || value === "") {
        if (field.required) {
          throw new Error(`${field.label} is required`);
        }
        return;
      }

      let processedValue: any = value;

      // Convert to satoshi if needed
      if (field.convertToSatoshi && field.type === "number") {
        processedValue = toSatoshi(parseFloat(value));
      }

      // Handle special field mappings
      if (selectedOperation === "bucketTransfer" && field.key === "assetId") {
        payload.asset = processedValue; // Use "asset" not "assetId"
      } else if (
        (selectedOperation === "extractToBucket" ||
          selectedOperation === "withdrawFromBucket" ||
          selectedOperation === "bankMint" ||
          selectedOperation === "bankTransfer") &&
        field.key === "assetId"
      ) {
        payload.asset = processedValue; // Use "asset" not "assetId"
      } else if (selectedOperation === "closePosition") {
        if (field.key === "quantity") {
          payload.closeSize = processedValue;
        } else if (field.key === "marketPrice") {
          payload.marketPrice = processedValue;
        } else {
          payload[field.key] = processedValue;
        }
      } else if (selectedOperation === "placeOrder") {
        if (field.key === "side") {
          payload.side = value.toUpperCase();
        } else if (field.key === "orderType") {
          payload.orderType = value.toUpperCase();
        } else {
          payload[field.key] = processedValue;
        }
      } else {
        payload[field.key] = processedValue;
      }
    });

    return payload;
  };

  // Prepare EIP-712 typed data
  const prepareTypedData = (payload: Record<string, any>) => {
    const domain = {
      name: "Morpheum",
      version: "1",
      chainId: chainId,
      verifyingContract:
        "0x0000000000000000000000000000000000000000" as `0x${string}`,
    };

    // Build types dynamically based on payload
    const types: Record<string, any[]> = {
      EIP712Domain: [
        { name: "name", type: "string" },
        { name: "version", type: "string" },
        { name: "chainId", type: "uint256" },
        { name: "verifyingContract", type: "address" },
      ],
      Message: [] as any[],
    };

    // Add fields to Message type
    Object.keys(payload).forEach((key) => {
      if (key === "action") {
        types.Message.push({ name: "action", type: "string" });
      } else if (key === "owner") {
        types.Message.push({ name: "owner", type: "address" });
      } else if (key === "nonce") {
        types.Message.push({ name: "nonce", type: "string" });
      } else if (key === "deadline") {
        types.Message.push({ name: "deadline", type: "uint256" });
      } else if (key === "address") {
        types.Message.push({ name: "address", type: "address" });
      } else if (
        typeof payload[key] === "string" &&
        payload[key].startsWith("0x")
      ) {
        types.Message.push({ name: key, type: "address" });
      } else if (
        typeof payload[key] === "number" ||
        /^\d+$/.test(payload[key])
      ) {
        types.Message.push({ name: key, type: "uint256" });
      } else {
        types.Message.push({ name: key, type: "string" });
      }
    });

    const message: Record<string, any> = {};
    Object.keys(payload).forEach((key) => {
      if (
        key === "owner" ||
        key === "address" ||
        (key.startsWith("0x") && key.length === 42)
      ) {
        message[key] = payload[key].toLowerCase();
      } else {
        message[key] = payload[key];
      }
    });

    return {
      domain,
      types,
      primaryType: "Message" as const,
      message,
    };
  };

  // Get private key from mnemonic (similar to BaseSignatureBuilder)
  const getPrivateKeyHex = async (): Promise<string> => {
    if (!mnemonic.trim()) {
      throw new Error("Mnemonic is required for WASM signing");
    }

    // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
    const masterNode = ethers.HDNodeWallet.fromPhrase(
      mnemonic.trim(),
      "", // no passphrase
      "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
    );

    // Validate that derived address matches
    if (masterNode.address.toLowerCase() !== ownerAddress.toLowerCase()) {
      throw new Error(
        `Address mismatch: derived ${masterNode.address} does not match ${ownerAddress}`,
      );
    }

    // Extract private key in the exact format Go's crypto.ToECDSA expects
    const privateKeyBigInt = BigInt(masterNode.privateKey);
    const SECP256K1_ORDER = BigInt(
      "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
    );

    if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
      throw new Error(
        `Invalid private key: must be between 1 and secp256k1 order - 1`,
      );
    }

    // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
    const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
    const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

    if (!/^[0-9a-f]{64}$/.test(paddedHex)) {
      throw new Error(
        `Invalid private key format: must be 64 lowercase hex characters (0-9, a-f) without 0x prefix`,
      );
    }

    return paddedHex;
  };

  // Handle signing with wallet
  const handleSign = async () => {
    if (!selectedOperation) {
      alert("Please select an operation first");
      return;
    }

    if (!ownerAddress) {
      alert("Please set owner address");
      return;
    }

    if (!nonce) {
      alert("Please generate nonce");
      return;
    }

    if (!deadline) {
      alert("Please generate deadline");
      return;
    }

    setIsSigning(true);
    setSigningResult(null);

    try {
      // Build payload
      const payload = buildPayload();

      // Add owner, nonce, deadline
      payload.owner = ownerAddress.toLowerCase();
      payload.nonce = nonce;
      payload.deadline = deadline;

      // Prepare typed data
      const typedData = prepareTypedData(payload);

      // Sign with wallet
      const signature = await signTypedDataAsync({
        domain: typedData.domain as any,
        types: typedData.types as any,
        primaryType: typedData.primaryType,
        message: typedData.message as any,
      });

      setSigningResult({
        success: true,
        signature,
        typedData: {
          payload,
          typedData,
        },
      });
    } catch (error: any) {
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setSigningResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        console.error("Error signing:", error);
        setSigningResult({
          success: false,
          error: error.message || "Unknown error during signing",
        });
      }
    } finally {
      setIsSigning(false);
    }
  };

  // Handle signing with WASM
  const handleSignWasm = async () => {
    if (!selectedOperation) {
      alert("Please select an operation first");
      return;
    }

    if (!ownerAddress) {
      alert("Please set owner address");
      return;
    }

    if (!nonce) {
      alert("Please generate nonce");
      return;
    }

    if (!deadline) {
      alert("Please generate deadline");
      return;
    }

    if (!mnemonic.trim()) {
      alert("Please enter mnemonic for WASM signing");
      return;
    }

    // Check if WASM is ready
    if (!mormSigner.isReady()) {
      try {
        await mormSigner.initialize();
        if (!mormSigner.isReady()) {
          alert("WASM module failed to initialize");
          return;
        }
      } catch (error: any) {
        alert(`Failed to initialize WASM: ${error.message || "Unknown error"}`);
        return;
      }
    }

    setIsSigningWasm(true);
    setSigningResult(null);

    try {
      // Build payload
      const payload = buildPayload();

      // Add owner, nonce, deadline
      payload.owner = ownerAddress.toLowerCase();
      payload.nonce = nonce;
      payload.deadline = deadline;

      // Get private key from mnemonic
      const privateKeyHex = await getPrivateKeyHex();

      // Build EIP712Tx structure for generateSingleSignature
      const now = Math.floor(Date.now() / 1000);
      const deadlineNum = parseInt(deadline, 10);

      const eip712Tx: Record<string, any> = {
        txId: "", // Empty txId for signing
        sigType: "ECDSA_LEGACY", // Signature type as string
        threshold: 1,
        timestamp: now,
        payload: payload,
        deadline: deadlineNum,
      };

      // Use generateSingleSignature to sign the EIP712Tx
      const result = await mormSigner.generateSingleSignature(
        JSON.stringify(eip712Tx),
        privateKeyHex,
        ownerAddress.toLowerCase(),
      );

      if (!result.success || !result.signature) {
        throw new Error(result.error || "Failed to generate signature");
      }

      // Prepare typed data for display
      const typedData = prepareTypedData(payload);

      setSigningResult({
        success: true,
        signature: result.signature,
        typedData: {
          payload,
          typedData,
          eip712Tx,
        },
      });
    } catch (error: any) {
      console.error("Error signing with WASM:", error);
      setSigningResult({
        success: false,
        error: error.message || "Unknown error during WASM signing",
      });
    } finally {
      setIsSigningWasm(false);
    }
  };

  const config = selectedOperation
    ? OPERATION_CONFIGS[selectedOperation]
    : null;

  return (
    <div className="component">
      <h3>Functional Signer</h3>
      <p>Sign EIP-712 messages for Morpheum protocol operations</p>

      {/* Step 1: Select Operation */}
      <div
        className={`agent-delegation-step ${selectedOperation ? "connected" : ""}`}
      >
        <h4>Step 1: Select Operation {selectedOperation && "✓"}</h4>

        {!selectedOperation ? (
          <div className="agent-delegation-wallet-buttons">
            {Object.entries(OPERATION_CONFIGS).map(([key, config]) => (
              <button
                key={key}
                className="button"
                onClick={() => setSelectedOperation(key as OperationType)}
              >
                {config.label}
              </button>
            ))}
          </div>
        ) : (
          <div>
            <p style={{ marginBottom: "1rem" }}>
              <strong>Selected:</strong> {config?.label}
            </p>
            <button
              className="button secondary"
              onClick={() => {
                setSelectedOperation(null);
                setFormData({});
                setSigningResult(null);
              }}
            >
              Change Operation
            </button>
          </div>
        )}
      </div>

      {/* Step 2: Configure Parameters */}
      {selectedOperation ? (
        <div
          className={`agent-delegation-step ${
            ownerAddress && nonce && deadline ? "connected" : ""
          }`}
        >
          <h4>
            Step 2: Configure Parameters{" "}
            {ownerAddress && nonce && deadline && "✓"}
          </h4>

          {/* Wallet Connection */}
          <div className="form-group">
            <label htmlFor="walletConnection">Wallet Connection:</label>
            {!isWalletConnected ? (
              <div>
                <button
                  className="button"
                  onClick={handleConnectWallet}
                  disabled={isConnecting || !injectedConnector}
                >
                  {isConnecting ? "Connecting..." : "Connect Wallet"}
                </button>
                {availableAddresses.length > 0 && !ownerAddress && (
                  <div style={{ marginTop: "1rem" }}>
                    <label>Select Address:</label>
                    {availableAddresses.map((address) => (
                      <button
                        key={address}
                        className="button secondary"
                        onClick={() => setOwnerAddress(address)}
                        style={{
                          display: "block",
                          marginTop: "0.5rem",
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                        }}
                      >
                        {address}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="agent-delegation-connected-info">
                <div>
                  <strong>Connected:</strong> {detectedWallet || "Wallet"}
                  {ownerAddress && (
                    <div className="agent-delegation-address">
                      Address: {ownerAddress}
                    </div>
                  )}
                </div>
                <button
                  className="button secondary"
                  onClick={() => {
                    disconnect();
                    setAvailableAddresses([]);
                    setOwnerAddress("");
                  }}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>

          {/* Mnemonic (for generating address) */}
          <div className="form-group">
            <label htmlFor="mnemonic">Mnemonic (for address generation):</label>
            <textarea
              id="mnemonic"
              value={mnemonic}
              onChange={(e) => setMnemonic(e.target.value)}
              placeholder="Enter mnemonic to generate address..."
              rows={2}
            />
            <button
              className="button secondary"
              onClick={generateOwnerAddress}
              style={{ marginTop: "0.5rem" }}
            >
              Generate Address from Mnemonic
            </button>
          </div>

          {/* Owner Address */}
          <div className="form-group">
            <label htmlFor="ownerAddress">Owner Address:</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                id="ownerAddress"
                type="text"
                value={ownerAddress}
                onChange={(e) => setOwnerAddress(e.target.value)}
                placeholder="0x..."
                style={{ flex: 1 }}
              />
              <button
                className="button secondary"
                onClick={generateOwnerAddress}
                disabled={!mnemonic.trim()}
              >
                Generate
              </button>
            </div>
          </div>

          {/* Nonce */}
          <div className="form-group">
            <label htmlFor="nonce">Nonce:</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                id="nonce"
                type="text"
                value={nonce}
                onChange={(e) => setNonce(e.target.value)}
                placeholder="Auto-generated timestamp"
              />
              <button className="button secondary" onClick={generateNonce}>
                Generate
              </button>
            </div>
          </div>

          {/* Deadline */}
          <div className="form-group">
            <label htmlFor="deadline">Deadline (Unix timestamp):</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                id="deadline"
                type="text"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                placeholder="Auto-generated (1 hour from now)"
              />
              <button className="button secondary" onClick={generateDeadline}>
                Generate
              </button>
            </div>
          </div>

          {/* Operation-specific fields */}
          {config && (
            <>
              <h5 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
                Operation Parameters:
              </h5>
              {config.fields.map((field) => (
                <div key={field.key} className="form-group">
                  <label htmlFor={field.key}>
                    {field.label}
                    {field.required && " *"}
                  </label>
                  {field.type === "select" ? (
                    <select
                      id={field.key}
                      value={formData[field.key] || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.key]: e.target.value,
                        })
                      }
                    >
                      <option value="">Select {field.label}</option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={field.key}
                      type={field.type}
                      value={formData[field.key] || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.key]: e.target.value,
                        })
                      }
                      placeholder={field.placeholder}
                    />
                  )}
                  {field.convertToSatoshi && (
                    <small className="agent-delegation-small-text">
                      Will be converted to satoshi format
                    </small>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 2: Configure Parameters</h4>
          <p className="agent-delegation-disabled-text">
            Please select an operation in Step 1 to continue.
          </p>
        </div>
      )}

      {/* Step 3: Sign */}
      {selectedOperation && ownerAddress && nonce && deadline ? (
        <div className="agent-delegation-step">
          <h4>Step 3: Sign Message</h4>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              className="button"
              onClick={handleSign}
              disabled={
                isSigning ||
                isSigningWasm ||
                !ownerAddress ||
                !nonce ||
                !deadline ||
                (config?.fields.some((f) => f.required && !formData[f.key]) ??
                  false)
              }
            >
              {isSigning ? "Signing..." : "Sign from Owner"}
            </button>
            <button
              className="button"
              onClick={handleSignWasm}
              disabled={
                isSigning ||
                isSigningWasm ||
                !ownerAddress ||
                !nonce ||
                !deadline ||
                !mnemonic.trim() ||
                (config?.fields.some((f) => f.required && !formData[f.key]) ??
                  false)
              }
            >
              {isSigningWasm ? "Signing..." : "Sign by Agent"}
            </button>
          </div>
        </div>
      ) : (
        <div className="agent-delegation-step disabled">
          <h4>Step 3: Sign Message</h4>
          <p className="agent-delegation-disabled-text">
            Please complete Step 2 to continue.
          </p>
        </div>
      )}

      {/* Result */}
      {signingResult && (
        <div
          className={`output ${signingResult.success ? "success" : "error"}`}
        >
          {signingResult.success ? (
            <>
              <strong>Signature:</strong>
              <br />
              {signingResult.signature}
              <br />
              <br />
              {signingResult.typedData && (
                <>
                  <strong>Payload & Typed Data:</strong>
                  <br />
                  <pre className="agent-delegation-pre">
                    {JSON.stringify(signingResult.typedData, null, 2)}
                  </pre>
                </>
              )}
            </>
          ) : (
            <>
              <strong>Error:</strong>
              <br />
              {signingResult.error}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default FunctionalSigner;
